import { View, Pressable, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { hapticSelection } from '@/utils/haptics';

export type TripFilterType = 'upcoming' | 'completed' | 'archived';

type FilterOption = {
  id: TripFilterType;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
};

type TripFilterBarProps = {
  selectedFilter: TripFilterType;
  onFilterChange: (filter: TripFilterType) => void;
  upcomingCount?: number;
  completedCount?: number;
  archivedCount?: number;
  tone?: 'default' | 'inverse';
};

const filters: FilterOption[] = [
  { id: 'upcoming', label: 'Upcoming', icon: 'calendar-outline' },
  { id: 'completed', label: 'Past', icon: 'checkmark-done-outline' },
  { id: 'archived', label: 'Archived', icon: 'archive-outline' },
];

export function TripFilterBar({
  selectedFilter,
  onFilterChange,
  upcomingCount,
  completedCount,
  archivedCount,
  tone = 'default',
}: TripFilterBarProps) {
  const theme = useTheme();
  const isInverse = tone === 'inverse';

  const getFilterCount = (filterId: TripFilterType): number | undefined => {
    switch (filterId) {
      case 'upcoming':
        return upcomingCount;
      case 'completed':
        return completedCount;
      case 'archived':
        return archivedCount;
      default:
        return undefined;
    }
  };

  const handleFilterPress = (filterId: TripFilterType) => {
    if (filterId !== selectedFilter) {
      hapticSelection();
      onFilterChange(filterId);
    }
  };

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: theme.spacing(0.5),
        paddingVertical: theme.spacing(0.25),
        gap: theme.spacing(0.5),
        flexGrow: 1,
      }}
    >
      {filters.map((filter) => {
        const isSelected = selectedFilter === filter.id;
        const count = getFilterCount(filter.id);
        const chipBg = isInverse
          ? isSelected
            ? 'rgba(255,255,255,0.18)'
            : 'rgba(255,255,255,0.10)'
          : isSelected
            ? theme.colors.primary
            : theme.colors.surface;
        const chipBorder = isInverse
          ? isSelected
            ? 'rgba(255,255,255,0.40)'
            : 'rgba(255,255,255,0.22)'
          : isSelected
            ? theme.colors.primary
            : theme.colors.borderMuted;
        const iconColor = isInverse
          ? theme.colors.surface
          : isSelected
            ? theme.colors.surface
            : theme.colors.textSecondary;
        const labelColor = isInverse
          ? theme.colors.surface
          : isSelected
            ? theme.colors.surface
            : theme.colors.text;

        return (
          <Pressable
            key={filter.id}
            onPress={() => handleFilterPress(filter.id)}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing(0.4),
              paddingHorizontal: theme.spacing(1.15),
              paddingVertical: theme.spacing(0.65),
              borderRadius: theme.radii.pill,
              backgroundColor: chipBg,
              borderWidth: 1,
              borderColor: chipBorder,
              opacity: pressed ? 0.9 : 1,
              transform: [{ scale: pressed ? 0.98 : 1 }],
              minHeight: 36,
              minWidth: 112,
            })}
            accessibilityRole="button"
            accessibilityLabel={`Filter by ${filter.label}${count !== undefined && count > 0 ? `, ${count} trips` : ''}`}
            accessibilityHint={`Shows only ${filter.label.toLowerCase()} trips`}
            accessibilityState={{ selected: isSelected }}
          >
            <Ionicons
              name={filter.icon}
              size={14}
              color={iconColor}
            />
            <ThemedText
              variant="label"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{
                fontSize: 12.5,
                color: labelColor,
                fontWeight: isSelected ? '700' : '600',
              }}
            >
              {filter.label}
            </ThemedText>
            {count !== undefined && (
              <View
                style={{
                  backgroundColor: isInverse
                    ? 'rgba(255,255,255,0.16)'
                    : isSelected
                      ? 'rgba(255,255,255,0.18)'
                      : theme.colors.primary + '08',
                  paddingHorizontal: theme.spacing(0.7),
                  paddingVertical: theme.spacing(0.25),
                  borderRadius: theme.radii.pill,
                  minWidth: 22,
                  alignItems: 'center',
                }}
              >
                <Caption
                  style={{
                    fontSize: 11.5,
                    color: isInverse ? theme.colors.surface : isSelected ? theme.colors.surface : theme.colors.primary,
                    fontWeight: '700',
                  }}
                >
                  {count}
                </Caption>
              </View>
            )}
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
